import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastStatus = 'success' | 'error' | 'warning' | 'info';

export type ToastPlacement = 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface ToastOptions {
  title?: string;
  description?: string;
  status?: ToastStatus;
  duration?: number;
  placement?: ToastPlacement;
  isClosable?: boolean;
  onCloseComplete?: () => void;
  render?: () => ReactNode;
}

export interface Toast extends ToastOptions {
  id: string;
}

interface ToastContextType {
  toasts: Toast[];
  show: (options: ToastOptions) => string;
  hide: (id: string) => void;
  hideAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const hide = useCallback((id: string) => {
    setToasts(prev => {
      const toast = prev.find(t => t.id === id);
      if (toast?.onCloseComplete) {
        toast.onCloseComplete();
      }
      return prev.filter(t => t.id !== id);
    });
  }, []);

  const show = useCallback(
    (options: ToastOptions): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const toast: Toast = {
        id,
        duration: 5000,
        placement: 'top',
        isClosable: true,
        ...options,
      };

      setToasts(prev => {
        // Limit to 3 toasts maximum
        const newToasts = [...prev, toast];
        if (newToasts.length > 3) {
          return newToasts.slice(-3);
        }
        return newToasts;
      });

      // Auto-hide after duration
      if (toast.duration && toast.duration > 0) {
        setTimeout(() => {
          hide(id);
        }, toast.duration);
      }

      return id;
    },
    [hide],
  );

  const hideAll = useCallback(() => {
    setToasts(prev => {
      prev.forEach(toast => {
        if (toast.onCloseComplete) {
          toast.onCloseComplete();
        }
      });
      return [];
    });
  }, []);

  const value: ToastContextType = {
    toasts,
    show,
    hide,
    hideAll,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};
